// La vista abierta sobrevive a recargar la pestaña (F5).
//
// Sin router no hay URL que recordar: la sesión sí sobrevivía (está en
// `sessionStorage`), pero la aplicación volvía a montar con `modulo = 'registro'` y
// cualquier rol —el Comité, Asuntos Regulatorios— aterrizaba en el formulario de
// notificación y perdía la pantalla en la que estaba (QA exploratorio, 14-sep-2026).
//
// Se guarda lo justo para volver: el módulo y, si lo había, el caso abierto en la
// evaluación o en el expediente. Vive en `sessionStorage` como la sesión, por lo mismo:
// muere con la pestaña y no cruza a otra.
//
// Lo que se lee se COMPRUEBA: el módulo tiene que ser uno conocido y permitido por la sesión,
// y un caso tiene que ser un UUID. Un valor editado a mano cae al aterrizaje.
//
// HU-32a (32a-6): lo permitido y el aterrizaje son los de `GET /sesion` (`modulos_permitidos`,
// `modulo_inicial`), no la lista por rol que tenía el frontend.

import { MODULOS } from '../data/navegacion';
import type { ModuloDeNavegacion } from '../data/navegacion';
import { esUuid } from './identificadores';

const CLAVE = 'fv_vista';

export type VistaPersistida = {
  modulo: string;
  evaluacionDe?: { casoId: string; pestana: string; desde: 'evaluacion' | 'aprobacion' } | null;
  expedienteDe?: string | null;
  /** Desde qué vista se abrió el expediente, para que «Volver» regrese ahí (25-sep-2026). */
  expedienteDesde?: string | null;
};

const esModuloDelMenu = (m: unknown): m is ModuloDeNavegacion =>
  typeof m === 'string' && Object.hasOwn(MODULOS, m);

/**
 * El módulo con el que arranca la sesión: la vista guardada si vale, si no `modulo_inicial`.
 * Sin aterrizaje (el servidor lo manda nulo), el primer módulo permitido.
 */
export function vistaInicial(permitidos: readonly string[], moduloInicial: string | null): VistaPersistida {
  const porDefecto: VistaPersistida = { modulo: moduloInicial ?? permitidos[0] ?? 'bandeja' };
  try {
    const crudo = sessionStorage.getItem(CLAVE);
    if (!crudo) return porDefecto;
    const guardada = JSON.parse(crudo) as Partial<VistaPersistida>;
    const modulo = guardada.modulo;
    const evaluacionDe = guardada.evaluacionDe && esUuid(guardada.evaluacionDe.casoId)
      && (guardada.evaluacionDe.desde === 'evaluacion' || guardada.evaluacionDe.desde === 'aprobacion')
      ? guardada.evaluacionDe : null;
    const expedienteDe = typeof guardada.expedienteDe === 'string' && esUuid(guardada.expedienteDe) ? guardada.expedienteDe : null;
    // El origen se comprueba como el módulo: una vista conocida, o la evaluación (que no
    // está en el menú). Un valor editado a mano deja el expediente sin botón, no roto.
    const expedienteDesde = esModuloDelMenu(guardada.expedienteDesde) || guardada.expedienteDesde === 'evaluacion_del_caso'
      ? guardada.expedienteDesde : null;
    if (modulo === 'evaluacion_del_caso' && evaluacionDe && permitidos.includes(evaluacionDe.desde)) {
      return { modulo, evaluacionDe, expedienteDe };
    }
    if (modulo === 'expediente' && expedienteDe) return { modulo, evaluacionDe, expedienteDe, expedienteDesde };
    if (modulo === 'destinatarios' && permitidos.includes('registro')) return { modulo };
    if (esModuloDelMenu(modulo) && permitidos.includes(modulo)) return { modulo, evaluacionDe, expedienteDe, expedienteDesde };
    return porDefecto;
  } catch {
    return porDefecto;
  }
}

export function guardarVista(vista: VistaPersistida): void {
  try {
    sessionStorage.setItem(CLAVE, JSON.stringify(vista));
  } catch {
    // Sin almacenamiento la vista no sobrevive a recargar; no merece romper nada.
  }
}

export function olvidarVista(): void {
  try {
    sessionStorage.removeItem(CLAVE);
  } catch {
    /* nada que olvidar */
  }
}
